// src/features/editor/components/studio/BlockPanelHeader.tsx
import { useState } from 'react'
import type { Block } from '@/types/blocks'
import { typeIcon, typeMeta } from './blockTypeLabels'
import { getTypeIconColor } from '@/features/editor/constants/blockTypeColors'
import { Button, cn } from '@teach/ui'
import { Trash2 } from 'lucide-react'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { screenDeleteConfirm } from './screenDeleteConfirm'
import { LOCK_REASON_TEXT } from './lockReasonText'
import type { BlockLockReason } from './useBlocksInRun'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: Block
  /** Позиционный ранг блока в списке урока (0-based) — из `useBlockIndex`,
   *  тот же плотный счёт, что видит миниатюра. НЕ `block.order`: удаление
   *  соседа не перенумеровывает `order` (Task 2 ревью, Important-1) — с
   *  дырами в `order` шапка называла бы не тот экран. `null` — позиция
   *  неизвестна (блоки урока ещё не приехали): диалог удаления тогда не
   *  называет номер вовсе, а не подставляет первый попавшийся. */
  index: number | null
  isDirty: boolean
  isSaving: boolean
  /** Блок сохраняется сам (`useBlockAutoSave`): кнопки «Сохранить» нет. */
  autoSave?: boolean
  isCourseAuthor: boolean
  /** Блок прямо сейчас занят прогоном, пишущим в блоки (`useBlocksInRun`) —
   *  сохранение/удаление гаснут, чтобы не писать в снимок, который патч
   *  прогона всё равно перезапишет (§6.2, §7.5). */
  locked?: boolean
  /** Причина блокировки — текст тултипа зависит от неё (см. `BlockRunNotice`). */
  lockReason?: BlockLockReason
  onSave: () => void
  onDelete: () => void
}

export const BlockPanelHeader = ({
  block,
  index,
  isDirty,
  isSaving,
  autoSave = false,
  isCourseAuthor,
  locked = false,
  lockReason = 'edit',
  onSave,
  onDelete,
}: Props) => {
  const m = useMessages(messages)
  const Icon = typeIcon(block)
  const { label, chipId } = typeMeta(block)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const deleteConfirm = screenDeleteConfirm(index, label)
  const lockedTitle = LOCK_REASON_TEXT[lockReason].headerTitle

  return (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-surface px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <div
          // Иконка типа без подложки — как в окне «Добавить блок».
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center',
            getTypeIconColor(chipId),
          )}
        >
          <Icon className="h-8 w-8" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-base font-bold text-foreground">{label}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {/* Автосохранение: кнопки нет, статус — общий индикатор в шапке редактора. */}
        {!autoSave && isDirty ? (
          <Button
            size="sm"
            onClick={onSave}
            disabled={isSaving || !isCourseAuthor || locked}
            title={locked ? lockedTitle : !isCourseAuthor ? m.noRights : undefined}
          >
            {isSaving ? m.saving : m.save}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setConfirmOpen(true)}
          disabled={!isCourseAuthor || locked}
          title={locked ? lockedTitle : !isCourseAuthor ? m.noRights : undefined}
          aria-label={m.a11yDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <DeleteConfirmDialog
        open={confirmOpen}
        title={deleteConfirm.title}
        description={deleteConfirm.description}
        onOpenChange={setConfirmOpen}
        onConfirm={onDelete}
      />
    </div>
  )
}
